import {
  useHistoryAssetsByIdQuery,
  useHistoryAssetsQuery,
  useHistoryOperationModesByIdQuery
} from '@domain/assets'
import {useScheduleQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, CircularProgress, Collapse, Skeleton, Stack} from '@mui/material'
import {
  AddEditAssetCommentDialog,
  AssetLabel,
  CommentsSidebar,
  useAssetCommentsQuery
} from '@widgets/planning-comments'
import {
  PlanningChartGantt,
  PlanningChartLabels,
  PlanningChartLegend,
  TimeScaleFn,
  ScheduleItemsContainer,
  getLocalizedDayMonthTimeFormat,
  toPixel,
  useAssetHistoryDataByScheduleItemId,
  usePlanningChartScheduleData,
  usePlanningChartStartEnd,
  useSelectedScheduleItemId
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, ProductionPlanningLSSettingsKeys} from '../../../consts'
import {
  useLocalStorageSettings,
  useAddScheduleItem,
  useUpdateScheduleItem,
  usePeriodicAutofillCheck,
  useSubmitSchedule
} from '../../../hooks'
import {CollapsibleCard} from '../../shared'
import {EditScheduleItemDialog} from '../../shared/scheduleGanttChart'

import {PlanningActionsContent} from './scheduleCardActions'

interface ProductionPlanningChartCardProps {
  plantCode: string
  startOfPlan: Moment
  endOfPlan: Moment
  dateLabelHeight: number
  hourlyPriceHeight: number
  labelColumnWidth: number
  cellHeight: number
  cellWidth: number
  hoursDisplayed: number
  xScale: TimeScaleFn
  hoursList: Moment[]
  shouldShowScheduleItemComments: boolean
  shouldShowAssetComments: boolean
}
const _ProductionPlanningChartCard = React.forwardRef<
  HTMLDivElement,
  ProductionPlanningChartCardProps
>(
  (
    {
      plantCode,
      startOfPlan,
      endOfPlan,
      dateLabelHeight,
      hourlyPriceHeight,
      labelColumnWidth,
      cellHeight,
      cellWidth,
      hoursDisplayed,
      xScale,
      hoursList,
      shouldShowScheduleItemComments,
      shouldShowAssetComments
    },
    ref
  ) => {
    const timezoneId = useUpmPlantTimezone()
    const {
      t,
      i18n: {language}
    } = useTranslation(FEATURE_NAMESPACE)

    const {startOfChart, endOfChart} = usePlanningChartStartEnd()
    const {setScheduleItemId} = useSelectedScheduleItemId()

    const {data: sortedAssets} = useHistoryAssetsQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan],
      sorted: true
    })
    const {scheduleId} = usePlanningChartScheduleData()
    const {data: schedule} = useScheduleQuery({
      plantId: plantCode,
      range: [startOfPlan, endOfPlan],
      scheduleId,
      useSideEffect: usePeriodicAutofillCheck
    })
    const scheduleLastSaved = schedule?.lastSaved

    const assetIds = sortedAssets?.map((asset) => asset.id)
    const {data: assetComments} = useAssetCommentsQuery(assetIds, !shouldShowAssetComments)

    const {settings: commentsSidebarSettings, updateSettings: setCommentsSidebarSettings} =
      useLocalStorageSettings(ProductionPlanningLSSettingsKeys.PlanningCommentsSidebarSettings, {
        isCommentSidebarOpen: false
      })
    const {isCommentSidebarOpen} = commentsSidebarSettings

    const {settings: chartCardSettings, updateSettings: setChartCardSettings} =
      useLocalStorageSettings(ProductionPlanningLSSettingsKeys.PlanningChartCardSettings, {
        isCardOpen: true
      })
    const {isCardOpen} = chartCardSettings
    const toggleCardOpen = () => setChartCardSettings({isCardOpen: !isCardOpen})

    const toggleCommentsSidebar = () =>
      setCommentsSidebarSettings({
        isCommentSidebarOpen: !isCommentSidebarOpen
      })

    const closeCommentsSidebar = React.useCallback(
      () => setCommentsSidebarSettings({isCommentSidebarOpen: false}),
      [setCommentsSidebarSettings]
    )

    const commentSidebarWidth = Math.max(cellWidth * hoursDisplayed * 0.25, 500)
    const isCommentSidebarEnabled = shouldShowScheduleItemComments || shouldShowAssetComments

    const {isPending: isPendingOperations} = useHistoryOperationModesByIdQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan]
    })
    const {isPending: isPendingAssets} = useHistoryAssetsByIdQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan]
    })

    const addScheduleItem = useAddScheduleItem({startOfPlan, endOfPlan})
    const {isPending: isScheduleSubmitting} = useSubmitSchedule()

    const historyAssetsByItemId = useAssetHistoryDataByScheduleItemId({
      schedule,
      timeFrame: [startOfPlan, endOfPlan]
    })
    const {updateScheduleItem} = useUpdateScheduleItem({
      startOfPlan,
      endOfPlan,
      assetDataByScheduleItemId: historyAssetsByItemId
    })

    return (
      <CollapsibleCard
        isCollapsed={!isCardOpen}
        onToggleCollapsed={toggleCardOpen}
        title={t('planning.planningSectionTitle')}
        subtitle={
          scheduleLastSaved &&
          t('planning.lastSaved', {
            date: getLocalizedDayMonthTimeFormat(
              moment.utc(scheduleLastSaved.savedAt),
              timezoneId,
              language
            ),
            name: scheduleLastSaved.savedBy
          })
        }
        headerContent={
          <Stack direction="row" sx={{justifyContent: 'flex-end'}}>
            <PlanningActionsContent
              toggleCommentsSidebar={toggleCommentsSidebar}
              isPlanningCardCollapsed={!isCardOpen}
              schedule={schedule}
              assets={sortedAssets}
              startOfChart={startOfChart}
              endOfChart={endOfChart}
            />
          </Stack>
        }
        {...dataTestId('production_planning_chart_card')}
      >
        <Collapse in={isCardOpen} sx={{position: 'relative'}}>
          <AddEditAssetCommentDialog
            startOfPlan={startOfPlan}
            endOfPlan={endOfPlan}
            areCommentsEnabled={shouldShowAssetComments}
          />
          {isCommentSidebarEnabled && schedule && sortedAssets && (
            <CommentsSidebar
              width={commentSidebarWidth}
              isOpen={isCommentSidebarOpen}
              closeSidebar={closeCommentsSidebar}
              isReadOnly={false}
              schedule={schedule}
              assets={sortedAssets}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              startOfChart={startOfChart}
              endOfChart={endOfChart}
              onSelectScheduleItem={setScheduleItemId}
            />
          )}
          <Stack
            sx={{overflow: 'hidden'}}
            spacing={2}
            {...dataTestId('planning_chart_card_content')}
          >
            <PlanningChartLegend
              shouldShowActualPricesLegend
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
            />
            <Stack direction="row">
              <Box sx={{mt: toPixel(dateLabelHeight * 2)}}>
                {sortedAssets ? (
                  <PlanningChartLabels
                    sortedAssets={sortedAssets}
                    hourlyPriceHeight={hourlyPriceHeight}
                    cellHeight={cellHeight}
                    width={labelColumnWidth}
                    shouldShowPriceInfo={true}
                    renderAssetLabel={(asset, labelWidth) => (
                      <AssetLabel
                        asset={asset}
                        comment={assetComments?.find(
                          ({commentsCategoryInstanceId}) =>
                            commentsCategoryInstanceId === asset.id.toString()
                        )}
                        width={labelWidth}
                        isReadOnly={false}
                        shouldShowComment={shouldShowAssetComments}
                      />
                    )}
                  />
                ) : (
                  <Stack sx={{pt: toPixel(hourlyPriceHeight)}}>
                    {Array.from({length: 10}).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="text"
                        width={labelColumnWidth}
                        height={cellHeight}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
              {isPendingOperations || isPendingAssets ? (
                <CircularProgress />
              ) : (
                <PlanningChartGantt
                  ref={ref}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  numberOfAssets={sortedAssets?.length}
                  dateLabelHeight={dateLabelHeight}
                  hourlyPriceHeight={hourlyPriceHeight}
                  cellHeight={cellHeight}
                  cellWidth={cellWidth}
                  hoursDisplayed={hoursDisplayed}
                  xScale={xScale}
                  hoursList={hoursList}
                  shouldShowCurrentTime
                  shouldRoundCurrentTime
                  shouldShowActualPricesIndicator
                  shouldShowExactPrices
                  shouldShowColorCodedElectricityPrices
                  addScheduleItem={addScheduleItem}
                  isScheduleSubmitting={isScheduleSubmitting}
                  renderScheduleItems={(params) => (
                    <ScheduleItemsContainer
                      {...params}
                      shouldShowFixedForOptimizerInfo
                      shouldShowColorCodedElectricityPrices
                      shouldShowExactElectricityPrices
                      shouldShowStartEndTime={false}
                      shouldShowScheduleItemComments={shouldShowScheduleItemComments}
                      editDialog={
                        <EditScheduleItemDialog startOfPlan={startOfPlan} endOfPlan={endOfPlan} />
                      }
                      updateScheduleItem={updateScheduleItem}
                      isScheduleSubmitting={isScheduleSubmitting}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
        </Collapse>
      </CollapsibleCard>
    )
  }
)
export const ProductionPlanningChartCard = React.memo(_ProductionPlanningChartCard)
ProductionPlanningChartCard.displayName = '<ProductionPlanningChartCard>'
